import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  SignInPage, SignInCard, SignInWordmark, SignInTagline,
  PrimaryButton, SecondaryButton, AlertBanner,
} from '../styled-components';
import { WaveformBackground } from '../components';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

const COMPLIANCE_OPTIONS = [
  { value: 'hipaa',   label: 'HIPAA',   hint: 'We handle patient health information' },
  { value: 'hitrust', label: 'HITRUST', hint: 'Our shared vendors must be HITRUST certified' },
  { value: 'pci_dss', label: 'PCI DSS', hint: 'We handle cardholder payment data' },
  { value: 'soc2',    label: 'SOC 2',   hint: 'Our shared vendors must meet SOC 2' },
  { value: 'gdpr',    label: 'GDPR',    hint: 'We operate in or serve the EU/UK' },
];

export function ComplianceOnboardingPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(compliance: string[]) {
    setSubmitting(true);
    setError(null);
    try {
      await fetchOrThrow('/dashboard/api/onboarding/compliance', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ compliance }),
      });
      navigate('/', { replace: true });
    } catch {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  function toggle(value: string) {
    setSelected((current) => (
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    ));
  }

  return (
    <SignInPage>
      <WaveformBackground />
      <SignInWordmark>
        <img src="/static/guava-logo.png" alt="Guava" style={{ height: 250, filter: 'grayscale(1) brightness(0.75)', margin: '-80px 0' }} />
      </SignInWordmark>
      <SignInTagline>Any compliance requirements we should know about?</SignInTagline>

      <div style={{ width: 420, maxWidth: 'calc(100vw - 40px)', position: 'relative', zIndex: 1 }}>
        <SignInCard style={{ gap: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {COMPLIANCE_OPTIONS.map(({ value, label, hint }) => {
              const checked = selected.includes(value);
              return (
                <label
                  key={value}
                  style={{
                    display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer',
                    border: `1px solid ${checked ? 'rgba(0,196,232,0.5)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 10, padding: '12px 14px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(value)}
                    style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#fff' }}>{label}</span>
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)' }}>{hint}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {error && (
            <AlertBanner variant="red" style={{ marginBottom: 0 }}>{error}</AlertBanner>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <SecondaryButton
              onClick={() => submit([])}
              disabled={submitting}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Skip for now
            </SecondaryButton>
            <PrimaryButton
              onClick={() => submit(selected)}
              disabled={submitting}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              {submitting ? 'Saving…' : 'Continue'}
            </PrimaryButton>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
            Select any that apply. Editable later in Settings.
          </div>
        </SignInCard>
      </div>
    </SignInPage>
  );
}
